import { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { PageHeader, DataTable, StatusBadge, DateRangeFilter, filterByDate } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DatePicker } from "../components/DatePicker";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Archive, ArrowCounterClockwise, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const EMPTY = {
  nom: "", prenom: "", telephone: "", email: "",
  type_personnel: "interne", poste: "", societe_employeur: "", siret_employeur: "",
  date_debut: "", date_fin: "", notes: "",
};

export default function Staff() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("interne");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null); // null = create mode, id = edit mode
  const [form, setForm] = useState(EMPTY);
  const [range, setRange] = useState({ start: "", end: "" });
  const [toDelete, setToDelete] = useState(null);

  const load = () =>
    api.get("/staff", { params: filter === "all" ? {} : { type_personnel: filter } }).then((r) => setList(r.data));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      nom: s.nom || "", prenom: s.prenom || "", telephone: s.telephone || "", email: s.email || "",
      type_personnel: s.type_personnel || "interne", poste: s.poste || "",
      societe_employeur: s.societe_employeur || "", siret_employeur: s.siret_employeur || "",
      date_debut: s.date_debut || "", date_fin: s.date_fin || "", notes: s.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.nom || !form.prenom) { toast.error("Nom et prénom requis"); return; }
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (editId) {
        await api.put(`/staff/${editId}`, payload);
        toast.success("Personnel modifié");
      } else {
        await api.post("/staff", payload);
        toast.success("Personnel créé");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const staffColumns = useMemo(() => [
    { title: "Nom", render: (s) => <b>{s.prenom} {s.nom}</b> },
    { title: "Type", render: (s) => <span className="capitalize">{s.type_personnel}</span> },
    { title: "Poste / Société", render: (s) => s.poste || s.societe_employeur || "-" },
    { title: "SIRET", render: (s) => s.siret_employeur || "-" },
    { title: "Téléphone", key: "telephone" },
    { title: "Début", render: (s) => fmtDate(s.date_debut) },
    { title: "Statut", render: (s) => <StatusBadge status={s.actif ? "active" : "archivee"} /> },
    {
      title: "Actions",
      render: (s) => (
        <div className="flex gap-2 justify-end">
          <Button
            size="sm"
            variant="outline"
            className="h-8 border-[#27272a] hover:bg-[#1a1a21] text-[#fbbf24]"
            onClick={() => openEdit(s)}
            data-testid={`edit-staff-${s.id}`}
            title="Modifier"
          >
            <PencilSimple size={14} />
          </Button>
          {s.actif ? (
            <Button size="sm" variant="outline" className="h-8 border-[#27272a] hover:bg-[#1a1a21] text-white"
              onClick={async () => { await api.post(`/staff/${s.id}/archive`); load(); }}
              data-testid={`archive-staff-${s.id}`}
              title="Archiver"
            >
              <Archive size={14} />
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-8 border-[#27272a] hover:bg-[#1a1a21] text-white"
              onClick={async () => { await api.post(`/staff/${s.id}/reactivate`); load(); }}
              data-testid={`reactivate-staff-${s.id}`}
              title="Restaurer"
            >
              <ArrowCounterClockwise size={14} />
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 border-[#27272a] hover:bg-[#3f1d1d] hover:border-[#f87171] text-[#f87171]"
            onClick={() => setToDelete(s)}
            data-testid={`delete-staff-${s.id}`}
            title="Supprimer"
          >
            <Trash size={14} />
          </Button>
        </div>
      ),
    },
  // `load` and `openEdit` are stable for the lifetime of this page; no
  // need to re-create columns on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <div>
      <PageHeader
        testId="staff-header"
        title="Personnel"
        subtitle="Salariés internes et agents de sécurité (suivi URSSAF)."
        actions={
          <div className="flex gap-3 items-center flex-wrap">
            <DateRangeFilter testId="staff-filter" start={range.start} end={range.end} onChange={setRange} />
            <Button data-testid="new-staff" onClick={openCreate} className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black">
              <Plus size={16} className="mr-2" /> Nouveau
            </Button>
          </div>
        }
      />
      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-[#121217] border border-[#27272a]">
          <TabsTrigger value="interne" data-testid="tab-interne">Salariés internes</TabsTrigger>
          <TabsTrigger value="securite" data-testid="tab-securite">Agents sécurité</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-staff">Tous</TabsTrigger>
        </TabsList>
      </Tabs>

      <DataTable
        columns={staffColumns}
        rows={(range.start || range.end) ? filterByDate(list, "date_debut", range.start, range.end) : list}
        rowKey="id"
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(EMPTY); } }}>
        <DialogContent className="bg-[#121217] border-[#27272a] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editId ? "Modifier le personnel" : "Nouveau personnel"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-xs">Type</Label>
              <Select value={form.type_personnel} onValueChange={(v) => setForm({ ...form, type_personnel: v })}>
                <SelectTrigger data-testid="type-select" className="bg-[#09090b] border-[#27272a]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121217] border-[#27272a] text-white">
                  <SelectItem value="interne">Interne</SelectItem>
                  <SelectItem value="securite">Sécurité</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {[
              ["prenom", "Prénom *"],
              ["nom", "Nom *"],
              ["telephone", "Téléphone"],
              ["email", "Email"],
              ["poste", "Poste"],
              ["societe_employeur", "Société employeur"],
              ["siret_employeur", "SIRET employeur"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="label-xs">{l}</Label>
                <Input data-testid={`staff-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-[#09090b] border-[#27272a]" />
              </div>
            ))}
            <div>
              <Label className="label-xs">Date début</Label>
              <DatePicker value={form.date_debut || ""} onChange={(v) => setForm({ ...form, date_debut: v })} />
            </div>
            <div className="col-span-2">
              <Label className="label-xs">Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#09090b] border-[#27272a]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditId(null); setForm(EMPTY); }} className="border-[#27272a] hover:bg-[#1a1a21] text-white">Annuler</Button>
            <Button data-testid="submit-staff" onClick={submit} className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black">
              {editId ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => { if (!v) setToDelete(null); }}>
        <AlertDialogContent className="bg-[#121217] border-[#27272a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">Supprimer l'employé ?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#a1a1aa]">
              Cette action supprime définitivement <b className="text-white">{toDelete?.prenom} {toDelete?.nom}</b> de la base.
              Si l'employé a des pointages enregistrés, la suppression sera bloquée — archivez plutôt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#27272a] text-white hover:bg-[#1a1a21]"
              data-testid="delete-staff-cancel"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#f87171] hover:bg-[#ef4444] text-white"
              data-testid="delete-staff-confirm"
              onClick={async () => {
                try {
                  await api.delete(`/staff/${toDelete.id}`);
                  toast.success("Employé supprimé");
                  setToDelete(null);
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erreur de suppression");
                }
              }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
